import React, { ChangeEvent, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from 'themes';
import { ActionItem } from '../Scope';
import {
  IdType,
  User,
  UserFormElement,
  UserFormParams,
  UserFormTypes,
  UserTypes
} from 'types/user-management';
import { v4 as uuidv4 } from 'uuid';
import UserForm from 'pages/UserManagementTwo/components/User/AddEditUser/UserForm';
import Collapse from 'rc-collapse';
import { HeaderWrapper } from '..';
import {
  MenuPanel,
  expandIcon
} from 'pages/MaintenanceServiceDashboard/MaintenanceQueryTableByFrequency';
import { useDeleteUserV2Mutation, useUpdateGroupUserMutation } from 'api';
import { ToastMsg } from 'common/components/Toast';
import { IcoTrash } from 'icons/indexed-icons/IcoTrash';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'common/components/ConfirmationModal';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import AddExistingUser from './AddExistingUser';
import { ContentAlign } from 'common/components/common-styled';
import { RadioButton, Typography } from 'components';

enum UserType {
  new_user = 'new_user',
  existing_user = 'existing_user',
  none = 'none'
}

const UserContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 0.063rem solid ${theme.colors.lightGrey4};
  margin-bottom: 1.063rem;
  margin-top: 1rem;

  .group-users-collapse {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .group-users-collapse .rc-collapse-item {
    padding: 0.825rem 0;
    border-bottom: 0.063rem solid ${theme.colors.lightGrey4};
    margin: 0;
    &:first-child {
      padding-top: 0;
    }
  }
`;
const NoUser = styled.div`
  text-align center;
  align-self: center;
  width: 4rem;
  font-family: Roboto;
  color: ${theme.colors.mediumGrey4};
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 1rem;
`;
export const ChildHeader = styled.div`
  font-family: Roboto;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding-left: 1rem;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;
const HeaderText = styled.div`
  color: ${({ theme }) => theme.colors.text.lightBlack};
  font-family: Roboto;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;
const HeaderIcon = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  height: 1.25rem;
`;
const UserFormWrapper = styled.div``;
const newUser: User = {
  firstName: '',
  lastName: '',
  email: '',
  emailAlert: false,
  textAlert: false,
  phone: ''
};
interface props {
  groupId?: IdType | string;
  groupUsers: UserFormElement[];
  setGroupUsers: (UserFormElements: UserFormElement[]) => void;
  handleAddExistingUser: (users: UserTypes[]) => void;
  existingGroupUsers?: UserTypes[];
}
const GroupUsers = ({
  groupId,
  groupUsers,
  setGroupUsers,
  handleAddExistingUser,
  existingGroupUsers
}: props): JSX.Element => {
  const [showUserForm, setShowUserForm] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { t } = useTranslation(['fpns']);
  const [userToDelete, setUserToDelete] = useState<UserFormElement | null>(null);
  const [userType, setUserType] = useState<UserType>();

  const groupUsersIds = useMemo(() => {
    return groupUsers.map((user) => user.id || '');
  }, [groupUsers.length]);

  const defaultUserFormElement: UserFormElement = {
    user: newUser,
    validationState: {},
    id: uuidv4()
  };
  const [userForm, setUserForm] = useState(defaultUserFormElement);
  const [deleteUser] = useDeleteUserV2Mutation();
  const [updateEditedUser] = useUpdateGroupUserMutation();
  const updateGroupUserList = (
    user: UserFormElement | UserFormTypes,
    isEditMode?: boolean,
    showUserForm?: boolean
  ): void => {
    if (isEditMode) {
      const updatedUsers = groupUsers.map((u) => {
        if (u.id === user.id) {
          u = { ...user } as UserFormElement;
        }
        return u;
      });
      handleUserEdit(user as UserFormElement, updatedUsers);
    } else {
      setGroupUsers([...groupUsers, user] as UserFormElement[]);
      setShowUserForm(showUserForm || false);
      if (showUserForm) {
        setUserForm(defaultUserFormElement);
      }
    }
  };
  const handleUserEdit = (form: UserFormElement, updatedUsers: UserFormElement[]): void => {
    const userParam: UserFormParams = {
      email: form.user.email,
      emailAlert: form.user.emailAlert,
      firstName: form.user.firstName,
      lastName: form.user.lastName,
      id: form.user.id,
      phone: form.user.phone,
      textAlert: form.user.textAlert
    };
    if (form.user.id) {
      updateEditedUser({ ...userParam })
        .unwrap()
        .then(() => {
          ToastMsg({
            heading: 'Success',
            message: 'User updated successfully!',
            type: 'success',
            position: 'top-center'
          });
          setGroupUsers([...updatedUsers] as UserFormElement[]);
        })
        .catch(() => {
          ToastMsg({
            heading: 'Error',
            message: 'Failed to update user.',
            type: 'error',
            position: 'top-center'
          });
        });
    } else {
      setGroupUsers([...updatedUsers] as UserFormElement[]);
      ToastMsg({
        heading: 'Success',
        message: 'New User updated successfully!',
        type: 'success',
        position: 'top-center'
      });
    }
  };
  const getUserPanelKeys = (): string[] => {
    const keys: string[] = [];
    groupUsers.forEach((panel) => {
      keys.push(panel.id as string);
    });
    return keys;
  };
  const onCancelUserAdd = (isEditMode: boolean, userId?: string): void => {
    if (!isEditMode) {
      setUserForm(defaultUserFormElement);
      setShowUserForm(false);
      setUserType(UserType.none); // Reset the user type when deleting the new user form
    } else {
      if (groupId) {
        const validUsers = groupUsers.filter((e) => e.user.id !== userId);
        deleteUser([{ id: userId, groups: [{ id: groupId }] as IdType[] }])
          .unwrap()
          .then(() => {
            ToastMsg({
              heading: 'Success',
              message: 'User has been successfully deleted.',
              type: 'success'
            });
            setGroupUsers([...validUsers]);
          })
          .catch(() => {
            ToastMsg({
              heading: 'Error',
              message: 'Failed to delete user.',
              type: 'error'
            });
          });
      } else {
        const validUsers = groupUsers.filter((e) => e.id !== userToDelete?.id);
        setGroupUsers([...validUsers]);
        setUserType(UserType.none);
      }
    }
  };

  const handleUserFormChange = (data: User | UserTypes) => {
    setUserForm({ ...userForm, user: data });
  };

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    setUserType(
      id === 'radio-existing-user'
        ? UserType.existing_user
        : id === 'radio-new-user'
        ? UserType.new_user
        : UserType.none
    );
    setShowUserForm(id === 'radio-new-user');
  };
  const [activePanelKeys, setActivePanelKeys] = useState<React.Key[]>(getUserPanelKeys());

  const helperText = `${t('are_you_sure_you_want_to_delete_the_user')}`;
  const title = `${t('delete_user')}`;
  return (
    <>
      <ActionItem style={{ margin: '1.5rem 0' }}>
        <Typography mb={'.5rem'} size={'0.875rem'} weight="semi-bold">
          {t('add_a_user')}
        </Typography>
        <ContentAlign justifyContent="space-between" gap="0">
          <RadioButton
            name={'user'}
            onChange={handleRadioChange}
            checked={userType === 'new_user'}
            id="new-user"
            label={t('add_new_user') as string}
            labelWeight={400}
          />
          <RadioButton
            name={'user'}
            onChange={handleRadioChange}
            checked={userType === UserType.existing_user}
            id="existing-user"
            label={t('add_existing_user') as string}
            labelWeight={400}
          />
        </ContentAlign>
      </ActionItem>
      {showUserForm ? (
        <UserFormWrapper>
          <FormHeader>
            <HeaderText>
              User&nbsp;{groupUsers && groupUsers.length + 1 + (existingGroupUsers?.length || 0)}
            </HeaderText>
            <HeaderIcon
              onClick={() => {
                onCancelUserAdd(false);
              }}
              title="Delete User"
            >
              <IcoTrash width={'24px'} />
            </HeaderIcon>
          </FormHeader>
          <UserForm
            userData={userForm}
            onUserSave={updateGroupUserList}
            isAddGroupUser={true}
            groupUsers={groupUsers}
            handleFormChange={handleUserFormChange}
          />
        </UserFormWrapper>
      ) : null}

      <AddExistingUser
        handleUserSelect={handleAddExistingUser}
        existingGroupUsers={existingGroupUsers}
        addedGroupUsers={groupUsersIds}
        showMultiDropdown={userType === UserType.existing_user}
      />
      <UserContainer>
        {groupUsers && groupUsers.length > 0 ? (
          <>
            <Collapse
              className="group-users-collapse"
              activeKey={activePanelKeys}
              onChange={(key: React.Key | React.Key[]) => {
                setActivePanelKeys(Array.isArray(key) ? key : [key]);
              }}
              destroyInactivePanel={true}
              expandIcon={expandIcon}
              accordion={true}
            >
              {groupUsers.map((panel) => {
                const header = (
                  <HeaderWrapper>
                    <ChildHeader>
                      {panel.user?.firstName}&nbsp;{panel.user?.lastName}
                    </ChildHeader>
                    <FormHeader>
                      <HeaderIcon
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowConfirmationModal(true);
                          setUserToDelete(panel);
                        }}
                        title="Delete User"
                      >
                        <IcoTrash width={'24px'} />
                      </HeaderIcon>
                    </FormHeader>
                  </HeaderWrapper>
                );
                return (
                  <MenuPanel
                    showArrow={true}
                    collapsible="header"
                    key={panel.id}
                    header={header}
                    mb={0}
                  >
                    {
                      <UserForm
                        userData={panel}
                        onUserSave={updateGroupUserList}
                        isEditMode={true}
                      />
                    }
                  </MenuPanel>
                );
              })}
            </Collapse>
          </>
        ) : existingGroupUsers?.length ? null : (
          <NoUser>No User</NoUser>
        )}
      </UserContainer>

      {showConfirmationModal && (
        <ConfirmationModal
          isConfirmPrompt
          isVisible={showConfirmationModal}
          type="warning"
          title={title}
          headerIcon={<TriangleWarning />}
          onConfirmCallback={() => {
            setShowConfirmationModal(false);
            if (userToDelete) {
              onCancelUserAdd(true, userToDelete.user.id);
            }
          }}
          onCancelCallback={() => {
            setShowConfirmationModal(false);
          }}
        >
          <p>{helperText}</p>
        </ConfirmationModal>
      )}
    </>
  );
};
export default GroupUsers;
